import React from 'react';

import { injectIntl, type WithIntlProps, type WrappedComponentProps } from 'react-intl-next';

import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { CompanyHubTextColorPlugin } from '../../../companyHubTextColorPluginType';

import { ToolbarCompanyHubTextColor, ToolbarType } from './ToolbarCompanyHubTextColor';

export interface Props {
	api: ExtractInjectionAPI<CompanyHubTextColorPlugin> | undefined;
	disabled?: boolean;
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent;
	editorView: EditorView;
	isReducedSpacing?: boolean;
	popupsBoundariesElement?: HTMLElement;
	popupsMountPoint?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
}

export const PrimaryToolbarComponent: React.FC<WithIntlProps<Props & WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<Props & WrappedComponentProps>;
} = injectIntl(
	({
		editorView,
		popupsMountPoint,
		popupsBoundariesElement,
		popupsScrollableElement,
		isReducedSpacing,
		dispatchAnalyticsEvent,
		disabled,
		api,
	}: Props & WrappedComponentProps) => {
		const {
			color,
			defaultColor,
			disabled: textColorDisabled,
		} = useSharedPluginStateWithSelector(api, ['companyHubTextColor'], (states) => ({
			color: states.companyHubTextColorState?.color,
			defaultColor: states.companyHubTextColorState?.defaultColor,
			disabled: states.companyHubTextColorState?.disabled,
		}));

		if (color === undefined || defaultColor === undefined) {
			return null;
		}
		return (
			<ToolbarCompanyHubTextColor
				pluginState={{
					color,
					defaultColor,
					disabled: textColorDisabled,
				}}
				editorView={editorView}
				popupsMountPoint={popupsMountPoint}
				popupsBoundariesElement={popupsBoundariesElement}
				popupsScrollableElement={popupsScrollableElement}
				isReducedSpacing={isReducedSpacing}
				dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				disabled={disabled}
				pluginInjectionApi={api}
				toolbarType={ToolbarType.PRIMARY}
			/>
		);
	},
);
